/**
 * IceRacingService - ICE Racing for parallel WebRTC connections
 *
 * Creates 5 PeerConnections with different ICE configurations in parallel,
 * the first P2P connection wins immediately. TURN connections wait until final timeout.
 *
 * All 5 PCs created simultaneously:
 * - p2p-1 to p2p-4: Pure P2P with reliable STUN servers (hitv, 03kan, douyu, google)
 * - p2p-5: P2P + TURN fallback
 *
 * Retry mechanism: If all P2P connections fail, retry up to 3 times (disableTurn mode)
 */

import { eventBus } from './EventBus';
import { signalingService } from './SignalingService';
import type { TurnProtocol, VideoCodecPreference } from '../types';
import type { PathId } from './PathObserver';

// ICE server configuration for a race candidate
export interface RaceCandidate {
  raceId: string;
  label: string;
  iceServers: RTCIceServer[];
  iceTransportPolicy?: RTCIceTransportPolicy;
}

// Quality test statistics
export interface QualityStats {
  packetsReceived: number;  // DataChannel 收到的测试包数
  packetsLost: number;      // DataChannel 丢失的测试包数（通过序列号间隙检测）
  throughputKbps: number;   // 吞吐量
  lastSeqNo: number;
  testStartTime: number;
  score: number;
  // Stats from getStats()
  rtt: number;              // RTT (ms) from candidate-pair
  // Heavy quality test 新增指标（仅 ENABLE_HEAVY_QUALITY_TEST=true 时维护）
  rttHistory: number[];          // 最近 N 次 RTT 采样，用于算抖动
  rttJitterMs: number;           // RTT 标准差（ms），TURN 排队时会飙升
  throughputHistory: number[];   // 每秒实测吞吐 (kbps)，用于算稳定性
  stableThroughputKbps: number;  // 历史最低 1 秒吞吐 / 平均吞吐 比值（百分比 0-100）
}

// Racing session state
export interface RacingSession {
  raceId: string;
  label: string;
  pc: RTCPeerConnection | null;
  dataChannel: RTCDataChannel | null;
  mouseChannel: RTCDataChannel | null;
  fsRpcChannel: RTCDataChannel | null;
  fileCtrlChannel: RTCDataChannel | null;   // File transfer control channel
  fileDataChannel: RTCDataChannel | null;   // File transfer data channel
  status: 'pending' | 'offer_received' | 'racing' | 'testing' | 'connected' | 'failed' | 'cancelled';
  startTime: number;
  connectTime?: number;
  connectionType?: 'p2p' | 'relay';
  turnServer?: string;          // 该 session 选中的 TURN 主机名（relay-only 配置即实际 Allocate）
  remoteStream?: MediaStream;
  iceState?: RTCIceConnectionState;
  pendingCandidates: RTCIceCandidateInit[];  // Queue for candidates before PC ready
  remoteCandidateCount: number;  // Count of received remote ICE candidates
  // Quality test fields
  qualityStats?: QualityStats;
}

// Winner event data
export interface RaceWinnerEvent {
  pc: RTCPeerConnection;
  dataChannel: RTCDataChannel | null;
  mouseChannel: RTCDataChannel | null;
  fsRpcChannel: RTCDataChannel | null;
  fileCtrlChannel: RTCDataChannel | null;   // File transfer control channel
  fileDataChannel: RTCDataChannel | null;   // File transfer data channel
  remoteStream: MediaStream | null;
  raceId: string;
  connectionType: 'p2p' | 'relay';
  connectTimeMs: number;
  iceServers: RTCIceServer[];
  turnProtocol?: 'TCP' | 'UDP';  // TURN connection protocol (if relay)
}

// Fisher-Yates shuffle for randomizing TURN server order
function shuffleArray<T>(arr: T[]): void {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
}

// STUN server pool
const STUN_POOL = [
  'stun:stun.douyucdn.cn:18000',
  'stun:106.12.251.52:3478',
  'stun:106.12.251.31:3478',
  'stun:106.13.248.6:3478',
  'stun:106.13.249.54:3478',
  'stun:106.12.71.140:3478',
  'stun:106.12.251.193:3478',
  'stun:180.76.162.88:3478',
  'stun:stun4.l.google.com:19302',
  'stun:stun.hitv.com:3478',
  'stun:rtc.03kan.com:3400',
  'stun:rtc2.03kan.com:3400',
  'stun:rtc3.03kan.com:3400',
];
 

// TURN server for fallback
  
const TURN_SERVER_UDP: RTCIceServer = {
  urls: 'turn:rtc.03kan.com:3400?transport=udp',
  username: 'hudd',
  credential: 'huddhudd',
};

const TURN_SERVER_UDP_2: RTCIceServer = {
  urls: 'turn:rtc2.03kan.com:3400?transport=udp',
  username: 'hudd',
  credential: 'huddhudd',
};

const TURN_SERVER_TCP: RTCIceServer = {
  urls: 'turn:rtc.03kan.com:3400?transport=tcp',
  username: 'hudd',
  credential: 'huddhudd',
};

const TURN_SERVER_TCP_2: RTCIceServer = {
  urls: 'turn:rtc2.03kan.com:3400?transport=tcp',
  username: 'hudd',
  credential: 'huddhudd',
};

const TURN_SERVER_UDP_3: RTCIceServer = {
  urls: 'turn:rtc3.03kan.com:3400?transport=udp',
  username: 'hudd',
  credential: 'huddhudd',
};

const TURN_SERVER_TCP_3: RTCIceServer = {
  urls: 'turn:rtc3.03kan.com:3400?transport=tcp',
  username: 'hudd',
  credential: 'huddhudd',
};

class IceRacingService {
  // 带 clientId 前缀的日志方法
  private get _tag() { return `[IceRacing][${signalingService.clientId}]`; }
  private _log(...args: any[]) { console.log(this._tag, ...args); }
  private _warn(...args: any[]) { console.warn(this._tag, ...args); }

  private sessions: Map<string, RacingSession> = new Map();
  private winner: RacingSession | null = null;
  private winReason: 'fast-win' | 'quality-select' | 'timeout-fallback' | 'forceRelay' | 'lanOnly' | null = null;
  private _lastHostStatus: import('./EventBus').EventMap['race:host_status'] | null = null;
  private _lastRaceSnapshot: { client: import('./EventBus').EventMap['race:status']; host: import('./EventBus').EventMap['race:host_status'] | null } | null = null;
  // B4：视频卡死(first-frame-stall)换路径 —— 下次 auto race 排除该 TURN 主机（一次性，用后即清）
  // 仅 auto/relayFirst 分支生效；forceRelay/disableTurn/lanOnly 不经此过滤，设了也不生效（无害）
  private excludeTurnHost: string | null = null;
  setExcludeTurnHost(host: string | null): void { this.excludeTurnHost = host; }
  get lastWinnerTurnHost(): string | null { return this.winner?.turnServer ?? null; }  // turnServer 已在 onSessionConnected 解析（relay-only 每 session 单 TURN，配置即实际 Allocate）
  private finalTimer: number | null = null;

  // host 硬错误 toast 去重（同一 race 内同一错误只提示一次，startRace 时重置）
  private hostErrorToasted: string | null = null;

  constructor() {
    // 持续记住最近一份 host 状态，供 declareWinner 抓快照时一并缓存（回看时显示 host 最终数据）
    eventBus.on('race:host_status', (d) => {
      this._lastHostStatus = d;
      // host 报告 race 不可能成功的硬错误 → 立即提示用户，不要干等到连接超时
      if (d.error && this.isRacing && this.hostErrorToasted !== d.error) {
        this.hostErrorToasted = d.error;
        const msg = d.error === 'internal_turn_unavailable'
          ? '内网 TURN 不可用：host 未发现可用的内网 forwarder，连接无法建立。请在设置中关闭「仅使用内网 TURN（测试用）」开关后重试'
          : `Host 连接错误: ${d.error}`;
        this._warn('Host reported race error:', d.error);
        eventBus.emit('ui:toast', { message: msg, type: 'error' });
      }
    });
  }
  private connectTimer: number | null = null;  // 连接超时计时器
  private statusTimer: number | null = null;
  private qualityReportTimer: number | null = null;  // 定期上报质量数据
  private raceStartTime: number = 0;
  private isRacing: boolean = false;
  private forceRelay: boolean = false;  // 强制中转模式
  private disableTurn: boolean = false;  // 禁用 TURN，只允许 P2P 直连
  private lanOnly: boolean = false;      // 局域网专用直连：只用 host 候选，不走 STUN/TURN（?lanOnly=1）
  private hostInternalTurnOnly: boolean = false;  // 测试用：host 忽略前端公网 TURN，仅用内网 forwarder
  private desiredPath: Exclude<PathId, 'p2p'> = 'direct';  // relay race 期望路径：direct / forward
  private turnProtocol: TurnProtocol = 'auto';
  // 当前 race 使用的 codec 偏好（由 startRace 从 WebRTCService.config 传入，不再依赖 localStorage）
  private currentVideoCodec: VideoCodecPreference = 'auto';
  private qualityTestStarted: boolean = false;  // 质量测试是否已开始
  private qualityTestExtended: boolean = false; // P2P 宽限期是否已使用
  private retryCount: number = 0;  // P2P 重试计数器
  private readonly MAX_RETRIES = 3;  // 最大重试次数（并发场景增加一次容错）

  // ICE Racing 质量测试改造总开关：true 走新逻辑（大流量 + 抖动/稳定性 + score-based fast-win）
  // 关掉则回退旧行为：100 包阈值、find()、4s 质量测试、不采集 jitter/stable_throughput
  // 仅作用于 IceRacingService 评判，不影响 P2PAssistService
  private readonly ENABLE_HEAVY_QUALITY_TEST = true;

  // Timing constants
  private readonly CONNECT_TIMEOUT = 30000;  // 30s 连接超时（增加以适应慢速STUN服务器）
  private currentConnectTimeout = 30000;     // 本次 race 实际使用的连接超时（lanOnly=6s）
  // QUALITY_TEST_TIME: heavy 模式下 host 发 4s，前端 finalTimer 收紧到 3s（数据足够给可信结论）；
  // legacy 模式保持 4s
  private readonly QUALITY_TEST_TIME = this.ENABLE_HEAVY_QUALITY_TEST ? 3000 : 4000;
  private readonly P2P_GRACE_TIME = 0;   // 禁用 P2P 宽限期，质量测试后直接选最佳线路

  // Relay-first 模式快速胜出阈值（auto 模式下，UDP 质量足够好就立即胜出，无需等满质量测试）
  private readonly FAST_WIN_LOSS_MAX = 2.0;     // 丢包率 < 2% 算"好"
  private readonly FAST_WIN_RTT_MAX = 150;      // RTT < 150ms 算"好"
  private readonly FAST_WIN_MIN_PACKETS = 100;  // legacy 模式：至少 100 个质量包才有判据
  // Heavy 模式新增门槛
  // ⚠️ 阈值与 host 端 StartQualityTest 的 target_kbps 必须配套：
  //    host fixed target=2000kbps → 实测吞吐上限 2000kbps，阈值需略低于此
  private readonly FAST_WIN_MIN_DURATION_MS = 2000;     // 至少跑满 2 秒稳态才允许 fast-win
  private readonly FAST_WIN_MIN_THROUGHPUT_KBPS = 1700; // 实测吞吐至少 ~85% target，扛住 buffer/抖动
  private readonly FAST_WIN_JITTER_MAX_MS = 30;         // RTT 抖动 < 30ms（排队的早期信号）
  // Heavy 模式 onFinalTimeout 选 winner 时的"合格门槛"
  private readonly QUALIFY_MIN_THROUGHPUT_KBPS = 1700;
  private readonly QUALIFY_MAX_LOSS_PCT = 5.0;
  private readonly QUALIFY_MAX_JITTER_MS = 50;
  private readonly RTT_HISTORY_SIZE = 8;
  private readonly THROUGHPUT_HISTORY_SIZE = 8;
  private relayFirstMode = false;  // auto 模式下：relay 首连，UDP 质量好即胜出，否则等 TCP 对比

  // All 5 candidates created at once (4 pure P2P + 1 with TURN fallback)
  // 减少候选数量以降低 STUN 服务器并发压力，提高成功率
  // 只使用最可靠的 STUN 服务器：hitv（华数）和 03kan（自有）
  private getAllCandidates(): RaceCandidate[] {
    const candidates: RaceCandidate[] = [
      // 最可靠的组合：两个可靠服务器
      {
        raceId: 'p2p-1',
        label: 'hitv+03kan',
        iceServers: [
          { urls: STUN_POOL[9] },  // stun.hitv.com - 华数，响应快
          { urls: STUN_POOL[10] }, // rtc.03kan.com - 自有服务器
        ],
      },
      // 备选组合：hitv + 斗鱼
      {
        raceId: 'p2p-2',
        label: 'hitv+Douyu',
        iceServers: [
          { urls: STUN_POOL[9] },  // stun.hitv.com
          { urls: STUN_POOL[0] },  // stun.douyucdn.cn - 斗鱼
        ],
      },
      // 备选组合：rtc2 + 百度
      {
        raceId: 'p2p-3',
        label: 'rtc2+Baidu',
        iceServers: [
          { urls: STUN_POOL[11] }, // rtc2.03kan.com - 自有2
          { urls: STUN_POOL[1] },  // 106.12.251.52 - 百度
        ],
      },
      // 备选组合：hitv + Google（国际备选）
      {
        raceId: 'p2p-4',
        label: 'hitv+Google',
        iceServers: [
          { urls: STUN_POOL[9] },  // stun.hitv.com
          { urls: STUN_POOL[8] },  // stun4.l.google.com
        ],
      },
    ];

    // P2P + TURN fallback：根据 turnProtocol 决定包含哪个 TURN
    // turnProtocol=tcp 时不包含 TURN-UDP fallback，避免 UDP TURN 意外胜出
    // 包含三个 TURN (rtc + rtc2 + rtc3)，随机排序消除 ICE relative_priority 偏好
    if (this.turnProtocol !== 'tcp') {
      const turnServers = [TURN_SERVER_UDP, TURN_SERVER_UDP_2, TURN_SERVER_UDP_3];
      shuffleArray(turnServers);
      candidates.push({
        raceId: 'p2p-5',
        label: 'hitv+TURN-UDP',
        iceServers: [
          { urls: STUN_POOL[9] },  // stun.hitv.com
          ...turnServers,
        ],
      });
    }

    return candidates;
  }

  // Relay-only candidates: 每个 TURN 服务器独立一个候选，iceTransportPolicy='relay'
  // 每个 session 只含 1 个 TURN，强制前端和 Host 双方 Allocate 到同一个 TURN 服务器，
  // 消除双方选不同 TURN 导致的双跳中转（Frontend → TURN-A → TURN-B → Host）
  private getRelayOnlyCandidates(turnProtocol: TurnProtocol = 'auto'): RaceCandidate[] {
    const udpCandidates: RaceCandidate[] = [
      {
        raceId: 'relay-rtc-udp',
        label: 'TURN-rtc-UDP',
        iceServers: [TURN_SERVER_UDP],
        iceTransportPolicy: 'relay',
      },
      {
        raceId: 'relay-rtc2-udp',
        label: 'TURN-rtc2-UDP',
        iceServers: [TURN_SERVER_UDP_2],
        iceTransportPolicy: 'relay',
      },
      {
        raceId: 'relay-rtc3-udp',
        label: 'TURN-rtc3-UDP',
        iceServers: [TURN_SERVER_UDP_3],
        iceTransportPolicy: 'relay',
      },
    ];
    const tcpCandidates: RaceCandidate[] = [
      {
        raceId: 'relay-rtc-tcp',
        label: 'TURN-rtc-TCP',
        iceServers: [TURN_SERVER_TCP],
        iceTransportPolicy: 'relay',
      },
      {
        raceId: 'relay-rtc2-tcp',
        label: 'TURN-rtc2-TCP',
        iceServers: [TURN_SERVER_TCP_2],
        iceTransportPolicy: 'relay',
      },
      {
        raceId: 'relay-rtc3-tcp',
        label: 'TURN-rtc3-TCP',
        iceServers: [TURN_SERVER_TCP_3],
        iceTransportPolicy: 'relay',
      },
    ];

    switch (turnProtocol) {
      case 'udp': return udpCandidates;
      case 'tcp': return tcpCandidates;
      case 'auto':
      default: return [...udpCandidates, ...tcpCandidates];
    }
  }

  /**
   * Pure P2P candidates used when TURN is disabled.
   * In multi-client contention, reducing per-client parallel candidates can
   * improve overall punching stability by lowering STUN/session pressure.
   * URL override: ?p2pPool=N
   */
  private getDisableTurnCandidates(): RaceCandidate[] {
    const pureP2PCandidates = this.getAllCandidates().filter(c => !c.iceServers.some(s => {
      const urls = Array.isArray(s.urls) ? s.urls : [s.urls];
      return urls.some(u => u.includes('turn:') || u.includes('turns:'));
    }));

    const params = new URLSearchParams(window.location.search);
    const poolOverride = parseInt(params.get('p2pPool') || '', 10);
    const poolSize = Number.isFinite(poolOverride) && poolOverride > 0
      ? Math.min(poolOverride, pureP2PCandidates.length)
      : pureP2PCandidates.length;

    const selected = pureP2PCandidates.slice(0, poolSize);
    console.log(`[IceRacing] TURN Disabled: using ${selected.length}/${pureP2PCandidates.length} pure P2P candidates`);
    return selected;
  }

  /**
   * LAN-only candidate: 局域网专用直连。
   * 空 iceServers + policy='all' → 浏览器只产生 host 候选（无 STUN→无 srflx，无 TURN→无 relay），
   * 媒体流强制走局域网。配合 handleCandidate 侧丢弃远端非 host 候选，保证不绕外网。
   * 适用前提：双方在同一局域网（由 ?lanOnly=1 显式声明，不做检测）。
   */
  private getLanOnlyCandidates(): RaceCandidate[] {
    return [{
      raceId: 'lan-direct',
      label: 'LAN-Direct',
      iceServers: [],
      iceTransportPolicy: 'all',
    }];
  }

  /**
   * 从 ICE candidate 字符串解析类型（host/srflx/prflx/relay）。
   * 空 candidate（end-of-candidates 哨兵）返回 null，由调用方决定放行。
   */
  private parseCandidateType(init: RTCIceCandidateInit): string | null {
    const s = init.candidate || '';
    if (s === '') return null;  // end-of-candidates
    const m = /\btyp\s+(\S+)/.exec(s);
    return m ? m[1] : null;
  }

  get racing(): boolean {
    return this.isRacing;
  }

  /** 最近一次 race 的完整快照，供连上后在诊断面板回看 */
  get lastRaceSnapshot(): { client: import('./EventBus').EventMap['race:status']; host: import('./EventBus').EventMap['race:host_status'] | null } | null {
    return this._lastRaceSnapshot;
  }

  /**
   * Check if ICE Racing is currently active
   */
  isActive(): boolean {
    return this.isRacing;
  }

  /**
   * Start ICE racing
   * @param forceRelay - 强制中转模式，跳过P2P阶段，只使用TURN
   */
  startRace(
    forceRelay: boolean = false,
    disableTurn: boolean = false,
    turnProtocol: TurnProtocol = 'auto',
    videoCodec: VideoCodecPreference = 'auto',
    hostInternalTurnOnly: boolean = false,
    desiredPath: Exclude<PathId, 'p2p'> = 'direct',
  ): void {
    if (this.isRacing) {
      this._warn('Already racing, ignoring startRace');
      return;
    }

    this.forceRelay = forceRelay;
    this.turnProtocol = turnProtocol;
    this.currentVideoCodec = videoCodec;
    this.hostInternalTurnOnly = hostInternalTurnOnly;
    this.desiredPath = hostInternalTurnOnly ? 'forward' : desiredPath;
    // 合并配置参数和 URL 参数
    const urlParams = new URLSearchParams(window.location.search);
    this.disableTurn = disableTurn || urlParams.get('disableTurn') === '1';
    // ?forceRelay=1 强制走 TURN 中转（用于测试 P2PAssist）
    if (urlParams.get('forceRelay') === '1') {
      this.forceRelay = true;
    }
    // ?lanOnly=1 局域网专用直连（最高优先级，覆盖 forceRelay/disableTurn）
    this.lanOnly = urlParams.get('lanOnly') === '1';

    const modeStr = this.lanOnly ? '(LAN-Only Direct)' : (forceRelay ? '(Force Relay Mode)' : (this.disableTurn ? '(TURN Disabled - P2P Only)' : ''));
    this._log('Starting ICE racing...', modeStr, 'desiredPath=', this.desiredPath);
    this.reset();
    this.isRacing = true;
    this.raceStartTime = Date.now();
    this.qualityTestStarted = false;
    this.retryCount = 0;  // 重置重试计数器
    this.hostErrorToasted = null;  // 新 race 重置 host 错误 toast 去重

    if (this.lanOnly) {
      // 局域网专用直连：只用 host 候选，不走 STUN/TURN，媒体强制走局域网
      this.relayFirstMode = false;
      this._log('LAN-Only Mode: host candidates only, no STUN/TURN');
      this.startAllCandidates(this.getLanOnlyCandidates());
    } else if (forceRelay) {
      // 强制中转模式：跳过P2P阶段，直接使用TURN
      this.relayFirstMode = false;
      this._log('Force Relay Mode: Using TURN only, no STUN');
      this.startAllCandidates(this.getRelayOnlyCandidates(this.turnProtocol));
    } else if (this.disableTurn) {
      // 禁用 TURN 模式：只使用纯 P2P 候选（并发场景默认降并发）
      this.relayFirstMode = false;
      this.startAllCandidates(this.getDisableTurnCandidates());
    } else {
      // 自动模式（icePolicy=auto）：首连只使用 relay 保证 2 秒内连上，
      // 连接成功后由 P2PAssistService 后台探测 P2P，成功后弹窗让用户选择是否切换
      // 启用 relayFirstMode：UDP 质量足够好就快速胜出，否则等 TCP 对比
      this.relayFirstMode = true;
      let relayCandidates = this.getRelayOnlyCandidates(this.turnProtocol);
      // B4-step2：视频卡死(first-frame-stall)重连时排除上次 winner 的 TURN 主机（按主机名，连带 UDP+TCP 两条）。
      // 注意：本过滤仅 auto/relayFirst 模式生效；forceRelay/disableTurn/lanOnly 分支不经此过滤，
      // setExcludeTurnHost 在这些模式下设了也不生效（无害）。
      if (this.excludeTurnHost) {
        const filtered = relayCandidates.filter(c => !c.iceServers.some(s => {
          const urls = Array.isArray(s.urls) ? s.urls : [s.urls];
          return urls.some(u => u.includes(this.excludeTurnHost!));
        }));
        if (filtered.length > 0) { relayCandidates = filtered; this._log(`Deadlock reconnect: excluded ${this.excludeTurnHost}`); }
        else this._log(`Deadlock reconnect: exclude ${this.excludeTurnHost} would empty pool, keep all`);
        this.excludeTurnHost = null;  // 一次性
      }
      this._log(`Auto Mode: Using relay-first strategy, ${relayCandidates.length} relay candidates (P2P upgrade by P2PAssist)`);
      this.startAllCandidates(relayCandidates);
    }

    // 连接超时：局域网直连应秒连，6s 内连不上即判定不在同一局域网/被隔离，快速失败
    this.currentConnectTimeout = this.lanOnly ? 6000 : this.CONNECT_TIMEOUT;
    this.connectTimer = window.setTimeout(() => this.onConnectTimeout(), this.currentConnectTimeout);

    // Start status broadcast (every 100ms for smooth UI updates)
    this.statusTimer = window.setInterval(() => this.broadcastStatus(), 100);
  }

  /**
   * Apply codec preferences to PeerConnection.
   * 优先级：URL 参数 > startRace 传入的 currentVideoCodec > auto
   * Uses setCodecPreferences() API to filter/reorder codecs.
   */
  private applyCodecPreferences(pc: RTCPeerConnection): void {
    const params = new URLSearchParams(window.location.search);
    const urlCodec = params.get('codec')?.toLowerCase();

    // codec 由 startRace 通过参数传入（来自 WebRTCService.config，运行期由 SettingsPanel 切换更新）
    // URL 参数最高优先级，方便临时测试
    const effectiveCodec: string = urlCodec || this.currentVideoCodec || 'auto';

    // auto 模式：保持现有行为（不设任何偏好，让浏览器按 offer 顺序选 answer codec）
    if (!effectiveCodec || effectiveCodec === 'auto') {
      this._log('Codec preference: auto (no setCodecPreferences, browser default)');
      return;
    }

    try {
      // Get video transceiver (the one receiving video from Host)
      const transceivers = pc.getTransceivers();
      const videoTransceiver = transceivers.find(t =>
        t.receiver.track?.kind === 'video' || t.mid === '0'
      );

      if (!videoTransceiver) {
        this._warn('No video transceiver found for codec preferences');
        return;
      }

      // Get available codecs from receiver capabilities
      const caps = RTCRtpReceiver.getCapabilities?.('video');
      if (!caps) {
        this._warn('RTCRtpReceiver.getCapabilities not supported');
        return;
      }

      const isAV1 = (c: RTCRtpCodecCapability) => /av1/i.test(c.mimeType);
      const isH265 = (c: RTCRtpCodecCapability) => /h265|hevc/i.test(c.mimeType);
      const isH264 = (c: RTCRtpCodecCapability) => /h264|avc/i.test(c.mimeType);

      const av1Codecs = caps.codecs.filter(isAV1);
      const h265Codecs = caps.codecs.filter(isH265);
      const h264Codecs = caps.codecs.filter(isH264);
      const otherCodecs = caps.codecs.filter(
        c => !isAV1(c) && !isH265(c) && !isH264(c)
      );

      let filteredCodecs: RTCRtpCodecCapability[];

      if (effectiveCodec === 'h264' || effectiveCodec === '264' || effectiveCodec === 'x264') {
        // H264 only - exclude all H265/HEVC/AV1 codecs
        filteredCodecs = [...h264Codecs, ...otherCodecs];
        this._log('Applied H264-only codec preference');
      } else if (effectiveCodec === 'h265' || effectiveCodec === '265') {
        // H265 priority
        filteredCodecs = [...h265Codecs, ...h264Codecs, ...otherCodecs];
        this._log('Applied H265-first preference,', h265Codecs.length, 'H265 codecs');
      } else if (effectiveCodec === 'av1') {
        // AV1 priority
        if (av1Codecs.length === 0) {
          this._warn('AV1 requested but browser does not support AV1 WebRTC, falling back to auto');
          return;  // 浏览器不支持 AV1，静默退回 auto（让浏览器按 offer 顺序选）
        }
        filteredCodecs = [...av1Codecs, ...h264Codecs, ...otherCodecs];
        this._log('Applied AV1-first preference,', av1Codecs.length, 'AV1 codecs');
      } else {
        this._warn('Unknown codec preference:', effectiveCodec);
        return;
      }

      // Apply codec preferences
      videoTransceiver.setCodecPreferences(filteredCodecs);
      this._log('setCodecPreferences applied with', filteredCodecs.length, 'codecs');
    } catch (e) {
      this._warn('Failed to apply codec preferences:', e);
    }
  }

  /**
   * Get supported video codecs from browser
   *
   * URL parameters:
   * - codec=h264 or codec=264: Force H264 only (disable H265)
   * - codec=x264: Force H264 with software encoder (disable H265)
   * - codec=h265 or codec=265: Force H265 priority (H264 as fallback)
   */
  private getSupportedCodecs(): string[] {
    // 读取 codec 偏好（URL 参数 > startRace 传入的 currentVideoCodec > auto）
    // codec 由 startRace 通过参数传入；URL 参数最高优先级，方便临时测试
    const params = new URLSearchParams(window.location.search);
    const urlCodec = params.get('codec')?.toLowerCase();
    const effectiveCodec = urlCodec || this.currentVideoCodec || 'auto';

    // ===== 明确选择模式 =====
    if (effectiveCodec === 'h264' || effectiveCodec === '264' || effectiveCodec === 'x264') {
      this._log('getSupportedCodecs: forced H264');
      return ['H264'];
    }

    if (effectiveCodec === 'h265' || effectiveCodec === '265') {
      this._log('getSupportedCodecs: forced H265 priority');
      return ['H265', 'H264'];
    }

    if (effectiveCodec === 'av1') {
      // 只有用户明确选 AV1 时，codecs[0]="AV1" 才会触发 host SetCodecPreferences
      this._log('getSupportedCodecs: forced AV1 priority');
      return ['AV1', 'H264'];
    }

    // ===== 自动模式：发 "auto" 让 host 自己根据能力决定 =====
    // host 收到 codecs[0]="auto" 后走自动策略：
    //   无 GPU + cores > 4 → AV1
    //   无 GPU + cores ≤ 4 → H264
    //   有 GPU → H265/H264（现有逻辑）
    this._log('getSupportedCodecs: auto (host decides)');
    return ['auto'];
  }

  /**
   * Start all candidates at once
   */
  private startAllCandidates(candidates: RaceCandidate[]): void {
    // Get browser supported codecs
    const codecs = this.getSupportedCodecs();

    // Send race_init message to Host with all candidates
    signalingService.send({
      type: 'race_init',
      codecs,  // Add supported codecs
      forceRelay: this.forceRelay,  // Tell Host if we're in forceRelay mode
      hostInternalTurnOnly: this.hostInternalTurnOnly,  // 测试用：host 忽略公网 TURN
      desiredPath: this.desiredPath,
      candidates: candidates.map(c => ({
        raceId: c.raceId,
        label: c.label,
        iceServers: c.iceServers.map(s => ({
          urls: s.urls,
          username: (s as RTCIceServer).username,
          credential: (s as RTCIceServer).credential,
        })),
        iceTransportPolicy: c.iceTransportPolicy,
      })),
    });

    // 打印每个 candidate 的 TURN 服务器配置（便于验证 rtc/rtc2/rtc3 均参与）
    for (const cand of candidates) {
      const turnUrls = cand.iceServers
        .filter(s => String(s.urls).startsWith('turn:'))
        .map(s => String(s.urls).replace('turn:', '').replace('?transport=udp', '(U)').replace('?transport=tcp', '(T)'));
      if (turnUrls.length > 0) {
        this._log(`${cand.raceId} TURN servers: [${turnUrls.join(', ')}]`);
      }
    }

    // Create local session records
    for (const cand of candidates) {
      this.sessions.set(cand.raceId, {
        raceId: cand.raceId,
        label: cand.label,
        pc: null,
        dataChannel: null,
        mouseChannel: null,
        fsRpcChannel: null,
        fileCtrlChannel: null,
        fileDataChannel: null,
        status: 'pending',
        startTime: Date.now(),
        pendingCandidates: [],
        remoteCandidateCount: 0,
      });
    }

    console.log(`[IceRacing] Started with ${candidates.length} candidates:`,
      candidates.map(c => c.raceId).join(', '));
  }

  /**
   * Collect stats from getStats() - RTT from candidate-pair
   * Note: No video stream during racing, so inbound-rtp has no data
   * Packet loss is detected via unreliable DataChannel sequence gaps
   */
  private async collectStats(session: RacingSession): Promise<void> {
    if (!session.pc || !session.qualityStats) return;

    try {
      const stats = await session.pc.getStats();
      stats.forEach((report) => {
        // Get RTT from candidate-pair
        if (report.type === 'candidate-pair' && report.state === 'succeeded') {
          const rtt = (report.currentRoundTripTime || 0) * 1000; // seconds to ms
          if (rtt > 0) {
            session.qualityStats!.rtt = rtt;
            // Heavy 模式：维护 RTT 历史窗口，用于算抖动
            if (this.ENABLE_HEAVY_QUALITY_TEST) {
              const hist = session.qualityStats!.rttHistory;
              hist.push(rtt);
              if (hist.length > this.RTT_HISTORY_SIZE) hist.shift();
              session.qualityStats!.rttJitterMs = this.computeJitter(hist);
            }
          }
        }
      });
    } catch (e) {
      console.warn(`[IceRacing] Failed to collect stats for ${session.raceId}:`, e);
    }
  }

  // 计算 RTT 标准差作为抖动指标。样本不足 2 个返回 0。
  private computeJitter(samples: number[]): number {
    if (samples.length < 2) return 0;
    const mean = samples.reduce((a, b) => a + b, 0) / samples.length;
    const variance = samples.reduce((a, b) => a + (b - mean) * (b - mean), 0) / samples.length;
    return Math.sqrt(variance);
  }

  // 维护每秒吞吐历史。每次 reportQualityStats 调用时基于 packetsReceived 增量计算。
  private updateThroughputHistory(session: RacingSession): void {
    const stats = session.qualityStats;
    if (!stats) return;
    const lastTotalPackets = (stats as any)._lastSampledPackets ?? 0;
    const lastSampleAt = (stats as any)._lastSampledAt ?? stats.testStartTime;
    const now = Date.now();
    const dtMs = now - lastSampleAt;
    if (dtMs < 400) return;  // 节流：至少 400ms 采一次（reportQualityStats 是 500ms）
    const dPackets = stats.packetsReceived - lastTotalPackets;
    const kbpsThisWindow = dtMs > 0 ? (dPackets * 1024 * 8 / dtMs) : 0;  // bits/ms == kbits/s
    const hist = stats.throughputHistory;
    hist.push(Math.round(kbpsThisWindow));
    if (hist.length > this.THROUGHPUT_HISTORY_SIZE) hist.shift();
    (stats as any)._lastSampledPackets = stats.packetsReceived;
    (stats as any)._lastSampledAt = now;
    // stableThroughputKbps = 历史最低 / 历史平均 × 100，体现吞吐稳定性
    if (hist.length >= 2) {
      const mean = hist.reduce((a, b) => a + b, 0) / hist.length;
      const minV = Math.min(...hist);
      stats.stableThroughputKbps = mean > 0 ? Math.round(minV / mean * 100) : 0;
    }
  }

  /**
   * Calculate quality score for a session
   * Heavy 模式权重: loss30% + throughput20% + rtt10% + jitter10% + stableTp10% + type15% (+ TCP -10 penalty)
   * Legacy 模式权重: loss35% + throughput25% + rtt15% + type25% (+ TCP -10 penalty)
   *
   * 关键差异：Heavy 模式新增 jitter 和 stableTp，专门捕捉 TURN 排队 / 突发卡顿征兆，
   * 这是 rtc2 vs rtc3 这种"小包心跳都很好但大流量扛不住"场景的核心区分项。
   */
  private calculateQualityScore(session: RacingSession): number {
    const stats = session.qualityStats;
    if (!stats || stats.packetsReceived === 0) {
      console.log(`  [Score] ${session.raceId}: no data (recv=0)`);
      return 0;
    }

    const totalPackets = stats.packetsReceived + stats.packetsLost;
    const lossRate = totalPackets > 0 ? stats.packetsLost / totalPackets : 0;
    const throughputScore = Math.min(stats.throughputKbps / 10000, 1);
    const rttScore = Math.max(0, 1 - stats.rtt / 500);

    let connectionTypeScore: number;
    let typeLabel: string;
    if (session.connectionType === 'p2p') {
      connectionTypeScore = 100;
      typeLabel = 'P2P';
    } else if (session.raceId.includes('-tcp')) {
      connectionTypeScore = 30;
      typeLabel = 'TCP-relay';
    } else {
      connectionTypeScore = 70;
      typeLabel = 'UDP-relay';
    }
    const tcpPenalty = session.raceId.includes('-tcp') ? 10 : 0;

    if (this.ENABLE_HEAVY_QUALITY_TEST) {
      // jitter 得分：0ms=100, 50ms+=0
      const jitterScore = Math.max(0, 1 - stats.rttJitterMs / 50);
      // stableTp 得分：stableThroughputKbps 是百分比 0-100（min/mean），未采到时按 100 处理（避免一开始被砍）
      const stableTpRatio = stats.throughputHistory.length >= 2
        ? Math.max(0, Math.min(1, stats.stableThroughputKbps / 100))
        : 1;

      const lossComponent       = 0.30 * (1 - lossRate) * 100;
      const throughputComponent = 0.20 * throughputScore * 100;
      const rttComponent        = 0.10 * rttScore * 100;
      const jitterComponent     = 0.10 * jitterScore * 100;
      const stableTpComponent   = 0.10 * stableTpRatio * 100;
      const typeComponent       = 0.15 * connectionTypeScore;

      const rawScore = lossComponent + throughputComponent + rttComponent +
                       jitterComponent + stableTpComponent + typeComponent;
      const finalScore = Math.max(0, Math.round((rawScore - tcpPenalty) * 10) / 10);

      const minTp = stats.throughputHistory.length > 0 ? Math.min(...stats.throughputHistory) : 0;
      const meanTp = stats.throughputHistory.length > 0
        ? Math.round(stats.throughputHistory.reduce((a, b) => a + b, 0) / stats.throughputHistory.length)
        : 0;
      console.log(
        `  [Score:heavy] ${session.raceId} (${typeLabel}): ` +
        `loss=${(lossRate * 100).toFixed(1)}%(${stats.packetsLost}/${totalPackets})→${lossComponent.toFixed(1)} | ` +
        `tp=${stats.throughputKbps}kbps→${throughputComponent.toFixed(1)} | ` +
        `rtt=${stats.rtt.toFixed(0)}ms→${rttComponent.toFixed(1)} | ` +
        `jitter=${stats.rttJitterMs.toFixed(0)}ms→${jitterComponent.toFixed(1)} | ` +
        `stableTp=${minTp}/${meanTp}kbps(${stats.stableThroughputKbps}%)→${stableTpComponent.toFixed(1)} | ` +
        `type=${connectionTypeScore}→${typeComponent.toFixed(1)} | ` +
        `raw=${rawScore.toFixed(1)} - penalty=${tcpPenalty} = ${finalScore}`
      );

      return finalScore;
    }

    // Legacy 路径
    const lossComponent = 0.35 * (1 - lossRate) * 100;
    const throughputComponent = 0.25 * throughputScore * 100;
    const rttComponent = 0.15 * rttScore * 100;
    const typeComponent = 0.25 * connectionTypeScore;
    const rawScore = lossComponent + throughputComponent + rttComponent + typeComponent;
    const finalScore = Math.max(0, Math.round((rawScore - tcpPenalty) * 10) / 10);

    console.log(
      `  [Score] ${session.raceId} (${typeLabel}): ` +
      `loss=${(lossRate * 100).toFixed(1)}%(${stats.packetsLost}/${totalPackets})→${lossComponent.toFixed(1)} | ` +
      `tp=${stats.throughputKbps}kbps→${throughputComponent.toFixed(1)} | ` +
      `rtt=${stats.rtt.toFixed(0)}ms→${rttComponent.toFixed(1)} | ` +
      `type=${connectionTypeScore}→${typeComponent.toFixed(1)} | ` +
      `raw=${rawScore.toFixed(1)} - penalty=${tcpPenalty} = ${finalScore}`
    );

    return finalScore;
  }

  /**
   * Connect timeout: No connection established within timeout
   */
  private onConnectTimeout(): void {
    if (this.winner || !this.isRacing) return;

    console.error(`[IceRacing] Connect timeout: No connection established within ${this.currentConnectTimeout}ms`);

    // 打印所有会话状态用于调试
    console.error('[IceRacing] Session states at connect timeout:');
    this.sessions.forEach((sess, id) => {
      console.error(`  ${id}: status=${sess.status}, iceState=${sess.iceState}`);
    });

    // 检查是否有任何 session 处于 connected 状态（可能只是 DC 还没打开）
    let hasConnectedSession = false;
    this.sessions.forEach(sess => {
      if (sess.iceState === 'connected' || sess.iceState === 'completed') {
        hasConnectedSession = true;
      }
    });

    // 如果有 connected 的 session，再等待一下 DC
    if (hasConnectedSession) {
      this._log('Found connected session, waiting 5s more for DataChannel...');
      setTimeout(() => this.onConnectTimeoutFinal(), 5000);
      return;
    }

    // P2P 模式下的重试逻辑
    if (this.disableTurn && this.retryCount < this.MAX_RETRIES) {
      this.retryCount++;
      console.warn(`[IceRacing] P2P connection failed, retrying (${this.retryCount}/${this.MAX_RETRIES})...`);

      // 清理当前 sessions
      this.cleanupAllSessions();

      // 通知 Host 取消当前 racing
      signalingService.send({ type: 'race_cancel' });

      // 延迟 1 秒后重试
      setTimeout(() => {
        if (!this.winner) {
          this._log('Retrying P2P connection...');
          this.retryP2PConnection();
        }
      }, 1000);
      return;
    }

    this.onConnectTimeoutFinal();
  }

  /**
   * Final connect timeout - no more retries
   */
  private onConnectTimeoutFinal(): void {
    if (this.winner || !this.isRacing) return;

    // 再次检查是否有 connected session
    let connectedSession: RacingSession | null = null;
    this.sessions.forEach(sess => {
      if ((sess.iceState === 'connected' || sess.iceState === 'completed') && !connectedSession) {
        connectedSession = sess;
      }
    });

    // 如果有 connected 的 session，直接宣布为 winner（即使 DC 没打开）
    if (connectedSession) {
      console.log(`[IceRacing] Using connected session ${connectedSession.raceId} as winner (DC may not be ready)`);
      this.declareWinner(connectedSession, 'timeout-fallback');
      return;
    }

    this.isRacing = false;

    // Cleanup
    if (this.statusTimer) {
      clearInterval(this.statusTimer);
      this.statusTimer = null;
    }
    if (this.qualityReportTimer) {
      clearInterval(this.qualityReportTimer);
      this.qualityReportTimer = null;
    }

    signalingService.send({ type: 'race_cancel' });
    eventBus.emit('race:failed', { reason: 'connect_timeout' });
  }

  /**
   * Retry P2P connection (for disableTurn mode)
   */
  private retryP2PConnection(): void {
    this.reset();
    this.isRacing = true;
    this.raceStartTime = Date.now();
    this.qualityTestStarted = false;

    // 使用纯 P2P 候选（并发场景默认降并发）
    const candidates = this.getDisableTurnCandidates();
    console.log(`[IceRacing] Retry #${this.retryCount}: Creating ${candidates.length} pure P2P candidates`);
    this.startAllCandidates(candidates);

    // 重新设置连接超时
    this.connectTimer = window.setTimeout(() => this.onConnectTimeout(), this.CONNECT_TIMEOUT);

    // Start status broadcast
    this.statusTimer = window.setInterval(() => this.broadcastStatus(), 100);
  }

  /**
   * Cleanup all sessions
   */
  private cleanupAllSessions(): void {
    this.sessions.forEach((sess, id) => {
      if (sess.pc) {
        sess.pc.close();
      }
    });
    this.sessions.clear();

    if (this.connectTimer) {
      clearTimeout(this.connectTimer);
      this.connectTimer = null;
    }
    if (this.statusTimer) {
      clearInterval(this.statusTimer);
      this.statusTimer = null;
    }
    if (this.finalTimer) {
      clearTimeout(this.finalTimer);
      this.finalTimer = null;
    }
    if (this.qualityReportTimer) {
      clearInterval(this.qualityReportTimer);
      this.qualityReportTimer = null;
    }
  }

  /**
   * Quality test complete: Select winner based on quality scores
   */
  private async onFinalTimeout(): Promise<void> {
    if (this.winner || !this.isRacing) return;

    // P2P 宽限期：如果只有 relay 候选连通，且还有 P2P 候选在尝试，延长等待
    if (!this.qualityTestExtended) {
      const hasP2PConnected = Array.from(this.sessions.values()).some(
        s => (s.status === 'connected' || s.status === 'testing') &&
             s.connectionType === 'p2p'
      );
      const hasP2PStillChecking = Array.from(this.sessions.values()).some(
        s => s.status === 'racing' && s.iceState === 'checking'
      );
      const hasRelayConnected = Array.from(this.sessions.values()).some(
        s => (s.status === 'connected' || s.status === 'testing') &&
             s.connectionType === 'relay'
      );

      if (!hasP2PConnected && hasP2PStillChecking && hasRelayConnected) {
        this.qualityTestExtended = true;
        console.log(`[IceRacing] P2P grace period: extending ${this.P2P_GRACE_TIME}ms for P2P candidates still checking`);
        this.finalTimer = window.setTimeout(() => this.onFinalTimeout(), this.P2P_GRACE_TIME);
        return;
      }
    }

    this._log('Quality test complete, selecting best session...');

    // Stop quality report timer
    if (this.qualityReportTimer) {
      clearInterval(this.qualityReportTimer);
      this.qualityReportTimer = null;
    }

    // Find all sessions that are valid candidates for winner selection
    const testingSessions: RacingSession[] = [];
    const failedSessions: string[] = [];
    this.sessions.forEach(sess => {
      // 只要是 connected 或 testing 状态就是有效候选
      // 质量数据只用于评分，没有数据也可以参与竞争
      const isConnectedOrTesting = sess.status === 'connected' || sess.status === 'testing';

      if (isConnectedOrTesting) {
        testingSessions.push(sess);
      } else {
        failedSessions.push(`${sess.raceId}:${sess.status}`);
      }
    });
    if (failedSessions.length > 0) {
      console.log(`[IceRacing] Failed/no-data sessions: ${failedSessions.join(', ')}`);
    }

    if (testingSessions.length === 0) {
      // 打印所有会话状态用于调试
      console.error('[IceRacing] No testing/connected sessions found. All session states:');
      this.sessions.forEach((sess, id) => {
        console.error(`  ${id}: status=${sess.status}, iceState=${sess.iceState}, connectionType=${sess.connectionType}`);
      });

      this.isRacing = false;
      signalingService.send({ type: 'race_cancel' });
      eventBus.emit('race:failed', { reason: 'all_failed' });
      return;
    }

    // Collect stats from all sessions (RTT from candidate-pair)
    await Promise.all(testingSessions.map(sess => this.collectStats(sess)));

    // Calculate scores after stats are collected
    testingSessions.forEach(sess => {
      if (sess.qualityStats) {
        sess.qualityStats.score = this.calculateQualityScore(sess);
      }
    });

    // 质量门槛：Heavy 模式收紧到 4000kbps & loss<5% & jitter<50ms
    const MIN_THROUGHPUT_KBPS = this.ENABLE_HEAVY_QUALITY_TEST
      ? this.QUALIFY_MIN_THROUGHPUT_KBPS : 3000;
    const MAX_LOSS_RATE = this.ENABLE_HEAVY_QUALITY_TEST
      ? this.QUALIFY_MAX_LOSS_PCT : 10.0;
    const MAX_JITTER_MS = this.ENABLE_HEAVY_QUALITY_TEST
      ? this.QUALIFY_MAX_JITTER_MS : Infinity;

    // 打印所有会话的质量数据（heavy 模式额外打 jitter / stable_throughput，便于排查）
    this._log('Quality results (loss% / throughput / RTT / jitter / stable):');
    testingSessions.forEach(s => {
      const stats = s.qualityStats;
      const totalPackets = (stats?.packetsReceived ?? 0) + (stats?.packetsLost ?? 0);
      const lossRate = totalPackets > 0
        ? (stats!.packetsLost / totalPackets) * 100
        : 0;
      const throughputKbps = stats?.throughputKbps ?? 0;
      const jitterMs = stats?.rttJitterMs ?? 0;
      const passed = throughputKbps >= MIN_THROUGHPUT_KBPS
        && lossRate < MAX_LOSS_RATE
        && jitterMs < MAX_JITTER_MS;
      const tpHist = stats?.throughputHistory ?? [];
      const tpMin = tpHist.length > 0 ? Math.min(...tpHist) : 0;
      const tpMean = tpHist.length > 0
        ? Math.round(tpHist.reduce((a, b) => a + b, 0) / tpHist.length)
        : 0;
      const stableTag = this.ENABLE_HEAVY_QUALITY_TEST
        ? `, stable_throughput=${tpMin}/${tpMean}kbps(${stats?.stableThroughputKbps ?? 0}%), jitter=${jitterMs.toFixed(0)}ms`
        : '';
      console.log(`  ${s.raceId} (${s.connectionType}): loss=${lossRate.toFixed(1)}% (${stats?.packetsLost ?? 0}/${totalPackets}), ${throughputKbps}kbps, RTT=${stats?.rtt?.toFixed(0) ?? 0}ms${stableTag} [score=${stats?.score ?? 0}] ${passed ? '✓PASS' : '✗FAIL'}`);
    });

    // 过滤达标的会话
    const qualifiedSessions = testingSessions.filter(s => {
      const stats = s.qualityStats;
      if (!stats) return false;
      const totalPackets = stats.packetsReceived + stats.packetsLost;
      const lossRate = totalPackets > 0 ? (stats.packetsLost / totalPackets) * 100 : 0;
      return stats.throughputKbps >= MIN_THROUGHPUT_KBPS
        && lossRate < MAX_LOSS_RATE
        && stats.rttJitterMs < MAX_JITTER_MS;
    });

    console.log(`[IceRacing] Quality threshold: throughput>=${MIN_THROUGHPUT_KBPS}kbps, loss<${MAX_LOSS_RATE}%, jitter<${MAX_JITTER_MS === Infinity ? 'inf' : MAX_JITTER_MS + 'ms'}`);
    console.log(`[IceRacing] Qualified: ${qualifiedSessions.map(s => s.raceId).join(', ') || '(none)'}`);
    console.log(`[IceRacing] Filtered out: ${testingSessions.filter(s => !qualifiedSessions.includes(s)).map(s => s.raceId).join(', ') || '(none)'}`);

    // 如果有达标的，从达标的中选；否则从所有 testing sessions 中选最好的
    let candidateSessions = qualifiedSessions.length > 0 ? qualifiedSessions : testingSessions;

    if (qualifiedSessions.length === 0) {
      this._warn('No session meets quality threshold, selecting best available from ALL sessions');
    }

    // Sort by: 1) DataChannel ready (ready first), 2) quality score (highest first)
    candidateSessions.sort((a, b) => {
      const dcReadyA = a.dataChannel && a.dataChannel.readyState === 'open' ? 1 : 0;
      const dcReadyB = b.dataChannel && b.dataChannel.readyState === 'open' ? 1 : 0;

      if (dcReadyA !== dcReadyB) {
        return dcReadyB - dcReadyA;
      }

      const scoreA = a.qualityStats?.score ?? 0;
      const scoreB = b.qualityStats?.score ?? 0;
      return scoreB - scoreA;
    });

    console.log(`[IceRacing] Final ranking:`);
    candidateSessions.forEach((s, i) => {
      const dc = s.dataChannel?.readyState === 'open' ? 'DC:✓' : 'DC:✗';
      console.log(`  #${i + 1} ${s.raceId} score=${s.qualityStats?.score ?? 0} ${dc}`);
    });

    const bestSession = candidateSessions[0];
    const dcStatus = bestSession.dataChannel?.readyState === 'open' ? 'DC:ready' : 'DC:waiting';
    console.log(`[IceRacing] >>> WINNER: ${bestSession.raceId} (${bestSession.connectionType}) score=${bestSession.qualityStats?.score ?? 0} [${dcStatus}]`);

    // Wait for DataChannel before declaring winner (same as onSessionConnected)
    await this.waitForReadyState(bestSession);
    if (this.winner) return; // Another session won while we were waiting

    this.declareWinner(bestSession, 'quality-select');
  }

  /**
   * Handle session connected
   */
  private async onSessionConnected(session: RacingSession): Promise<void> {
    if (this.winner) {
      // Already have a winner
      if (this.winner.raceId === session.raceId) {
        // This is the winner, don't close it (ICE state changed from connected to completed)
        return;
      }
      // Close non-winner connection
      if (session.pc) {
        session.pc.close();
      }
      session.status = 'cancelled';
      return;
    }

    // Prevent double-call when ICE state changes from 'connected' to 'completed'
    // This would reset qualityStats to 0
    if (session.status === 'connected' || session.status === 'testing') {
      return;
    }

    session.status = 'connected';
    session.connectTime = Date.now() - session.startTime;
    session.connectionType = await this.detectConnectionType(session.pc!);

    // 解析该 session 选中的 TURN 主机名（relay-only 每 session 只配 1 个 TURN，配置即实际 Allocate）
    if (session.connectionType === 'relay' && session.pc) {
      const turnUrl = (session.pc.getConfiguration().iceServers ?? [])
        .flatMap(s => Array.isArray(s.urls) ? s.urls : [s.urls])
        .find(u => /^turns?:/.test(u));
      if (turnUrl) session.turnServer = turnUrl.replace(/^turns?:/, '').split('?')[0].split(':')[0];
    }

    console.log(`[IceRacing] Session ${session.raceId} ICE connected (${session.connectionType}) in ${session.connectTime}ms`);

    // 提前初始化 qualityStats，这样 waitForReadyState 期间收到的质量包不会丢失
    // （quality_ready 可能在 DC open 后立即发出，Host 立即开始发包）
    session.status = 'testing';
    if (!session.qualityStats) {
      session.qualityStats = {
        packetsReceived: 0,
        packetsLost: 0,
        throughputKbps: 0,
        lastSeqNo: -1,
        testStartTime: Date.now(),
        score: 0,
        rtt: 0,
        rttHistory: [],
        rttJitterMs: 0,
        throughputHistory: [],
        stableThroughputKbps: 0,
      };
    }

    // Wait for DataChannel and video track before declaring winner
    // IMPORTANT: Must wait even in forceRelay mode, otherwise keyboard won't work
    // because Host-created dataChannel may not be ready yet
    await this.waitForReadyState(session);

    if (this.winner) {
      if (session.pc) {
        session.pc.close();
      }
      session.status = 'cancelled';
      return;
    }

    // forceRelay / lanOnly mode: 单候选，DC ready 即夺冠，跳过质量测试（无比较对象，质量测试纯属空等）
    if (this.forceRelay || this.lanOnly) {
      const tag = this.lanOnly ? 'LAN-Only' : 'forceRelay';
      console.log(`[IceRacing] ${tag} WINS: ${session.raceId} in ${session.connectTime}ms`);
      this.declareWinner(session, this.lanOnly ? 'lanOnly' : 'forceRelay');
      return;
    }

    if (this.winner) {
      if (session.pc) {
        session.pc.close();
      }
      session.status = 'cancelled';
      return;
    }

    // 进入质量测试阶段（qualityStats 已在上面初始化，不再重置）
    const isP2P = session.connectionType === 'p2p';
    const typeLabel = isP2P ? 'P2P' : 'TURN';
    // 第一个连接成功时：清除连接超时，启动质量测试计时器
    if (!this.qualityTestStarted) {
      this.qualityTestStarted = true;
      console.log(`[IceRacing] ${session.raceId} (${typeLabel}) connected in ${session.connectTime}ms, starting quality test`);

      // 清除连接超时
      if (this.connectTimer) {
        clearTimeout(this.connectTimer);
        this.connectTimer = null;
      }

      // 启动质量测试计时器
      this.finalTimer = window.setTimeout(() => this.onFinalTimeout(), this.QUALITY_TEST_TIME);
    }

    // Start quality report timer if not already started
    if (!this.qualityReportTimer) {
      this.qualityReportTimer = window.setInterval(() => this.reportQualityStats(), 500);
    }
  }

  /**
   * Wait for DataChannel and video track to be ready
   *
   * 注意：racing 模式下 Host add_video=false，video track 要等 winner 选出后
   * AddVideoTrack + Renegotiate 才会出现。所以 forceRelay/relayFirstMode 下不等
   * video，只等 DC，避免凭空多花 1.5s 死等 video 超时。
   */
  private async waitForReadyState(session: RacingSession): Promise<void> {
    // racing 模式下 video 在 winner 后才有，relay-first/forceRelay/lanOnly 不等 video
    // （lanOnly 单候选直连，video 同样由夺冠后 Renegotiate 补上，不能死等否则白白超时 1.5s）
    const requireVideo = !this.forceRelay && !this.relayFirstMode && !this.lanOnly;
    // [TCP 修复 B] TURN-TCP 路径 SCTP-over-DTLS 慢，DCEP 通常 1-3s 才到，800ms 不够
    // - disableTurn (P2P only): 5000ms
    // - relay-*-tcp: 5000ms
    // - 其它需视频: 1500ms
    // - 其它纯 DC: 800ms
    let maxWait: number;
    if (this.disableTurn) {
      maxWait = 5000;
    } else if (session.raceId.includes('-tcp')) {
      maxWait = 5000;
    } else if (requireVideo) {
      maxWait = 1500;
    } else {
      maxWait = 800;
    }
    const checkInterval = 50;
    let waited = 0;

    while (waited < maxWait) {
      // Check if we already have a winner
      if (this.winner) return;

      // Check if DataChannel is ready
      const dcReady = !!(session.dataChannel && session.dataChannel.readyState === 'open');
      // Require a live remote video track, not just a non-null stream reference.
      const videoTrack = session.remoteStream?.getVideoTracks()[0];
      const videoReady = !!(videoTrack && videoTrack.readyState === 'live');

      const ready = dcReady && (requireVideo ? videoReady : true);
      if (ready) {
        return;
      }


      await new Promise(r => setTimeout(r, checkInterval));
      waited += checkInterval;
    }

    // Timeout - proceed anyway with what we have
    const dcReady = !!(session.dataChannel && session.dataChannel.readyState === 'open');
    const videoTrack = session.remoteStream?.getVideoTracks()[0];
    const videoReady = !!(videoTrack && videoTrack.readyState === 'live');
    console.warn(`[IceRacing] ${session.raceId} ready timeout, proceeding with DC:${dcReady} Video:${videoReady}`);
  }

  /**
   * Handle quality test packet from Host
   * Format: { type: "quality_test", raceId: string, seq: number, total: number, ts: number, payload: string }
   */
  private handleQualityPacket(session: RacingSession, data: { type: string; raceId: string; seq: number; total: number; ts: number; payload?: string }): void {
    // Initialize quality stats if not already done (fallback)
    if (!session.qualityStats) {
      session.qualityStats = {
        packetsReceived: 0,
        packetsLost: 0,
        throughputKbps: 0,
        lastSeqNo: -1,
        testStartTime: Date.now(),
        score: 0,
        rtt: 0,
        rttHistory: [],
        rttJitterMs: 0,
        throughputHistory: [],
        stableThroughputKbps: 0,
      };
      session.status = 'testing';
    }

    const stats = session.qualityStats;


    stats.packetsReceived++;

    // Calculate lost packets (check for gaps in sequence)
    // lastSeqNo tracks the HIGHEST seq ever seen, not the last received
    // UDP packets can arrive out-of-order; reordered packets are NOT lost
    if (data.seq > stats.lastSeqNo) {
      // New highest seq - check for gap (potential loss)
      if (stats.lastSeqNo >= 0 && data.seq > stats.lastSeqNo + 1) {
        stats.packetsLost += data.seq - stats.lastSeqNo - 1;
      }
      stats.lastSeqNo = data.seq;
    } else {
      // Out-of-order: this packet was previously counted as "lost"
      if (stats.packetsLost > 0) stats.packetsLost--;
    }

    // Calculate throughput (bytes per second → kbps)
    const elapsedSec = (Date.now() - stats.testStartTime) / 1000;
    if (elapsedSec > 0) {
      // Assume each packet is ~1KB for throughput calculation
      const bytesReceived = stats.packetsReceived * 1024;
      stats.throughputKbps = Math.round((bytesReceived * 8) / elapsedSec / 1000);
    }

  }

  /**
   * Report quality stats to Host via signaling
   */
  private reportQualityStats(): void {
    this.sessions.forEach(session => {
      if (session.status === 'testing' && session.qualityStats) {
        // Heavy 模式：滚动维护吞吐稳定性 + 持续刷新 RTT 历史
        if (this.ENABLE_HEAVY_QUALITY_TEST) {
          this.updateThroughputHistory(session);
          // 异步采 RTT，不阻塞 reportQualityStats
          this.collectStats(session).catch(() => { /* ignore */ });
        }
        signalingService.send({
          type: 'race_quality',
          raceId: session.raceId,
          packetsReceived: session.qualityStats.packetsReceived,
          packetsLost: session.qualityStats.packetsLost,
          throughputKbps: session.qualityStats.throughputKbps,
        });
      }
    });

    // relay-first 模式（auto 模式）：在每次质量上报时检查是否可以提前选出 winner
    this.evaluateFastWin();
  }

  /**
   * 判断 session 的质量是否"足够好"可以快速胜出
   * Heavy 模式：duration≥2s & throughput≥3500kbps & loss<2% & rtt<150ms & jitter<30ms
   * Legacy 模式：丢包率 < FAST_WIN_LOSS_MAX% AND RTT < FAST_WIN_RTT_MAX AND packets >= FAST_WIN_MIN_PACKETS
   */
  private isQualityGoodEnough(session: RacingSession): boolean {
    const stats = session.qualityStats;
    if (!stats) return false;

    const total = stats.packetsReceived + stats.packetsLost;
    const lossRate = total > 0 ? (stats.packetsLost / total) * 100 : 0;
    const rttOk = stats.rtt > 0 && stats.rtt < this.FAST_WIN_RTT_MAX;
    const lossOk = lossRate < this.FAST_WIN_LOSS_MAX;

    if (this.ENABLE_HEAVY_QUALITY_TEST) {
      // Heavy: 必须积累足够稳态数据 + 实测吞吐过线 + RTT 抖动可控
      const elapsedMs = Date.now() - stats.testStartTime;
      if (elapsedMs < this.FAST_WIN_MIN_DURATION_MS) return false;
      if (stats.throughputKbps < this.FAST_WIN_MIN_THROUGHPUT_KBPS) return false;
      if (stats.rttJitterMs > this.FAST_WIN_JITTER_MAX_MS) return false;
      return rttOk && lossOk;
    }

    // Legacy: 100 包阈值
    if (total < this.FAST_WIN_MIN_PACKETS) return false;
    return rttOk && lossOk;
  }

  /**
   * relay-first 快速胜出评估（仅在 relayFirstMode=true 时生效）
   *
   * Heavy 模式：在所有合格的 UDP 候选里挑评分最高（不再"先达标先赢"），
   *   且要求至少 2 个 UDP 已 testing（避免单条抢跑）；超 5 秒仍只有 1 条则放行兜底。
   * Legacy 模式：保留原 find()、首达标即胜逻辑。
   */
  private evaluateFastWin(): void {
    if (!this.relayFirstMode || this.winner) return;

    const testingSessions = Array.from(this.sessions.values())
      .filter(s => s.status === 'testing' && s.qualityStats);
    if (testingSessions.length === 0) return;

    const udpSessions = testingSessions.filter(s => s.raceId.includes('-udp'));

    if (this.ENABLE_HEAVY_QUALITY_TEST) {
      const elapsedSinceRaceStart = Date.now() - this.raceStartTime;
      const minPeers = elapsedSinceRaceStart > 5000 ? 1 : 2;  // 超 5s 兜底放行单条
      const qualifiedUdp = udpSessions.filter(s => this.isQualityGoodEnough(s));
      if (qualifiedUdp.length > 0 && udpSessions.length >= minPeers) {
        // 给每个合格 UDP 计算分数，挑最高（不再"先达标先赢"）
        qualifiedUdp.forEach(s => {
          if (s.qualityStats) s.qualityStats.score = this.calculateQualityScore(s);
        });
        const bestUdp = qualifiedUdp.reduce((a, b) =>
          (a.qualityStats?.score ?? 0) >= (b.qualityStats?.score ?? 0) ? a : b);
        const stats = bestUdp.qualityStats!;
        const total = stats.packetsReceived + stats.packetsLost;
        const lossRate = total > 0 ? (stats.packetsLost / total) * 100 : 0;
        this._log(`UDP fast-win[heavy] (${bestUdp.raceId}): score=${stats.score} ` +
          `loss=${lossRate.toFixed(1)}% tp=${stats.throughputKbps}kbps ` +
          `rtt=${stats.rtt.toFixed(0)}ms jitter=${stats.rttJitterMs.toFixed(0)}ms ` +
          `peers=${udpSessions.length}/${minPeers}`);
        this.declareWinner(bestUdp, 'fast-win');
        return;
      }
      // UDP 不达标但 TCP 已 testing → 提前对比
      // ⚠️ 修复 (批一-修复)：fallback 必须等过 FAST_WIN_MIN_DURATION_MS 才触发，
      // 否则 reportQualityStats 第一次（~500ms）就被 fallback 抢跑 onFinalTimeout，
      // 导致 fast-win 2 秒门槛形同虚设、jitter/stableTp 全是 0/100、winner 选择
      // 退化为只比 throughput 总量。
      const anyTcp = testingSessions.some(s => s.raceId.includes('-tcp'));
      if (udpSessions.length > 0 && anyTcp) {
        const maxTestingElapsed = Math.max(
          ...testingSessions.map(s => s.qualityStats
            ? Date.now() - s.qualityStats.testStartTime : 0)
        );
        if (maxTestingElapsed < this.FAST_WIN_MIN_DURATION_MS) {
          // 还没等够稳态时间，让 finalTimer 兜底（heavy 模式 3s）。不打日志避免刷屏
          return;
        }
        this._log(`UDP quality not good enough[heavy], TCP also ready, triggering comparison early ` +
          `(maxElapsed=${maxTestingElapsed}ms)`);
        if (this.finalTimer) {
          clearTimeout(this.finalTimer);
          this.finalTimer = null;
        }
        this.onFinalTimeout();
      }
      return;
    }

    // Legacy 路径
    const bestUdp = udpSessions.find(s => this.isQualityGoodEnough(s));
    if (bestUdp) {
      const stats = bestUdp.qualityStats!;
      const total = stats.packetsReceived + stats.packetsLost;
      const lossRate = total > 0 ? (stats.packetsLost / total) * 100 : 0;
      this._log(`UDP fast-win (${bestUdp.raceId}): loss=${lossRate.toFixed(1)}% rtt=${stats.rtt.toFixed(0)}ms packets=${total}`);
      this.declareWinner(bestUdp, 'fast-win');
      return;
    }

    const anyTcp = testingSessions.some(s => s.raceId.includes('-tcp'));
    if (udpSessions.length > 0 && anyTcp) {
      this._log(`UDP quality not good enough, TCP also ready, triggering comparison early`);
      if (this.finalTimer) {
        clearTimeout(this.finalTimer);
        this.finalTimer = null;
      }
      this.onFinalTimeout();
    }
  }

  /**
   * Declare winner and cleanup
   */
  private declareWinner(session: RacingSession, reason: 'fast-win' | 'quality-select' | 'timeout-fallback' | 'forceRelay' | 'lanOnly' = 'quality-select'): void {
    if (this.winner) return;

    this.winner = session;
    this.winReason = reason;
    this.isRacing = false;

    // Clear timers
    if (this.finalTimer) {
      clearTimeout(this.finalTimer);
      this.finalTimer = null;
    }
    if (this.connectTimer) {
      clearTimeout(this.connectTimer);
      this.connectTimer = null;
    }
    if (this.qualityReportTimer) {
      clearInterval(this.qualityReportTimer);
      this.qualityReportTimer = null;
    }

    const totalTime = Date.now() - this.raceStartTime;
    console.log(`[IceRacing] Winner: ${session.raceId} (${session.connectionType}) in ${session.connectTime}ms (total: ${totalTime}ms)`);

    // Notify Host
    signalingService.send({
      type: 'race_result',
      winnerId: session.raceId,
      winnerType: session.connectionType,
      connectTimeMs: session.connectTime,
    });

    // 缓存最后一次 race 快照（必须在删除落败 session 之前，保留全部候选用于回看）
    this._lastRaceSnapshot = { client: this.buildStatusPayload(), host: this._lastHostStatus };

    // Close and cleanup all other sessions
    this.sessions.forEach((sess, id) => {
      if (id !== session.raceId) {
        // Close all channels
        if (sess.dataChannel) sess.dataChannel.close();
        if (sess.mouseChannel) sess.mouseChannel.close();
        // Close PeerConnection
        if (sess.pc) {
          sess.pc.close();
        }
        sess.status = 'cancelled';
        // Remove from map
        this.sessions.delete(id);
      }
    });

    // Get ICE servers for this session
    const allCandidates = [...this.getAllCandidates(), ...this.getRelayOnlyCandidates(this.turnProtocol)];
    const config = allCandidates.find(c => c.raceId === session.raceId);

    // Detect TURN protocol from ICE servers
    let turnProtocol: 'TCP' | 'UDP' | undefined;
    if (session.connectionType === 'relay' && config?.iceServers) {
      for (const server of config.iceServers) {
        const urls = Array.isArray(server.urls) ? server.urls : [server.urls];
        for (const url of urls) {
          if (url.includes('turn:') || url.includes('turns:')) {
            if (url.includes('transport=tcp')) {
              turnProtocol = 'TCP';
              break;
            } else if (url.includes('transport=udp')) {
              turnProtocol = 'UDP';
            }
          }
        }
        if (turnProtocol === 'TCP') break;
      }
      // Default to UDP if no transport specified
      if (!turnProtocol) turnProtocol = 'UDP';
    }

    // Emit winner event
    const winnerEvent: RaceWinnerEvent = {
      pc: session.pc!,
      dataChannel: session.dataChannel,
      mouseChannel: session.mouseChannel,
      fsRpcChannel: null,         // 懒加载：用到时在当前 PC 上创建
      fileCtrlChannel: null,      // 懒加载
      fileDataChannel: null,      // 懒加载
      remoteStream: session.remoteStream || null,
      raceId: session.raceId,
      connectionType: session.connectionType!,
      connectTimeMs: session.connectTime || 0,
      iceServers: config?.iceServers || [],
      turnProtocol,
    };

    eventBus.emit('race:winner', winnerEvent);

    // Send final status update
    this.broadcastFinalStatus();
  }

  /**
   * Detect connection type (P2P or relay)
   * 同时记录 relay 连接使用的 TURN 服务器 URL
   */
  private async detectConnectionType(pc: RTCPeerConnection): Promise<'p2p' | 'relay'> {
    // [C] 快路径：iceTransportPolicy='relay' 时浏览器只收集 relay 候选，
    // 数据物理上必经 TURN，不可能 P2P 直连。这是事实而非推断，
    // 无需读 stats、零时序依赖。覆盖 relayFirstMode / forceRelay。
    if (pc.getConfiguration().iceTransportPolicy === 'relay') {
      return 'relay';
    }
    // 走到这里说明 policy 必为 'all'（disableTurn 纯 P2P，或 auto 非 relay 候选），
    // 检测失败时按此场景默认 'p2p' 是正确的兜底方向。
    const fallback: 'p2p' | 'relay' = 'p2p';

    try {
      const stats = await pc.getStats();

      // [A] 定位"真正选中"的候选对，不再死等 state==='succeeded'：
      // iceConnectionState 变 connected 时，被提名 pair 的 stats.state
      // 往往还是 'in-progress'（relay 经 TURN 中转确认更慢），
      // 旧逻辑此刻命中 0 个 succeeded pair → 误判为 p2p。
      let selectedPair: any = null;

      // 1. 优先用 transport.selectedCandidatePairId 精确定位
      stats.forEach((report) => {
        if (report.type === 'transport' && report.selectedCandidatePairId) {
          const p = stats.get(report.selectedCandidatePairId);
          if (p) selectedPair = p;
        }
      });

      // 2. 退化：nominated pair → succeeded pair → 任意 pair
      if (!selectedPair) {
        let nominated: any = null;
        let succeeded: any = null;
        let anyPair: any = null;
        stats.forEach((report) => {
          if (report.type === 'candidate-pair') {
            if (report.nominated && !nominated) nominated = report;
            if (report.state === 'succeeded' && !succeeded) succeeded = report;
            if (!anyPair) anyPair = report;
          }
        });
        selectedPair = nominated || succeeded || anyPair;
      }

      if (!selectedPair) {
        this._warn(`detectConnectionType: stats 中暂无 candidate-pair，回退=${fallback}`);
        return fallback;
      }

      let isRelay = false;
      let turnServerUrl = '';
      [selectedPair.localCandidateId, selectedPair.remoteCandidateId].forEach((cid: string | undefined) => {
        if (!cid) return;
        const c = stats.get(cid);
        if (c && c.candidateType === 'relay') {
          isRelay = true;
          // url 字段包含产生该 relay candidate 的 TURN 服务器地址
          if (c.url) turnServerUrl = c.url;
        }
      });

      if (isRelay && turnServerUrl) {
        this._log(`Relay connection using TURN server: ${turnServerUrl}`);
      }

      return isRelay ? 'relay' : 'p2p';
    } catch (e) {
      // [A] 检测失败按 policy 兜底，不再硬编码 'p2p'
      this._warn('Failed to detect connection type:', e);
      return fallback;
    }
  }

  /**
   * Handle offer from Host for a specific raceId
   */
  async handleOffer(raceId: string, offer: RTCSessionDescriptionInit): Promise<void> {
    const session = this.sessions.get(raceId);
    if (!session) {
      this._warn('Session not found for offer:', raceId);
      return;
    }

    if (this.winner) {
      // 如果是 winner 的 renegotiate offer，用现有 PC 处理
      if (raceId === this.winner.raceId && this.winner.pc) {
        this._log('Renegotiate offer for winner:', raceId);
        try {
          await this.winner.pc.setRemoteDescription(offer);

          // 应用 codec 偏好（winner renegotiate 是视频 track 第一次出现的时机）
          // Racing 阶段 offer 只有 m=application，没有 m=video，所以 line 1564 的
          // applyCodecPreferences 调用找不到 video transceiver 会早 return。
          // winner renegotiate 时 host 才 AddTrack(video)，offer 里有 m=video，
          // setRemoteDescription 后 getTransceivers 才能拿到 video transceiver。
          this.applyCodecPreferences(this.winner.pc);

          const answer = await this.winner.pc.createAnswer();
          await this.winner.pc.setLocalDescription(answer);

          // Send answer to Host
          signalingService.send({
            type: 'webrtcviewer',
            kind: 'answer',
            raceId,
            message: answer,
          });
          this._log('Renegotiate answer sent for:', raceId);
        } catch (e) {
          console.error('[IceRacing] Renegotiate failed:', e);
        }
        return;
      }
      this._log('Already have winner, ignoring offer for:', raceId);
      return;
    }

    // Find ICE config for this raceId
    const allCandidates = [...this.getAllCandidates(), ...this.getRelayOnlyCandidates(this.turnProtocol), ...this.getLanOnlyCandidates()];
    const config = allCandidates.find(c => c.raceId === raceId);
    if (!config) {
      this._warn('Config not found for:', raceId);
      return;
    }

    session.status = 'offer_received';
    console.log(`[IceRacing] Received offer for ${raceId} (${config.label})`);

    try {
      // Create PeerConnection with specific ICE config
      const pc = new RTCPeerConnection({
        iceServers: config.iceServers,
        iceTransportPolicy: config.iceTransportPolicy || 'all',
        bundlePolicy: 'max-bundle',
        rtcpMuxPolicy: 'require',
      });
      session.pc = pc;

      // mouse / fs-rpc / file-ctrl / file-data 通道全部改为：
      // - mouse: 由 Host 创建，通过 ondatachannel 接收
      // - fs-rpc / file-ctrl / file-data: 懒加载，用到时在当前 PC 上创建

      // Set up ICE candidate handling
      pc.onicecandidate = (event) => {
        if (event.candidate) {
          signalingService.send({
            type: 'webrtcviewer',
            kind: 'candidate',
            raceId,
            message: event.candidate.toJSON(),
          });
        }
      };

      // Monitor ICE connection state
      pc.oniceconnectionstatechange = () => {
        const state = pc.iceConnectionState;
        session.iceState = state;

        if (state === 'connected' || state === 'completed') {
          this.onSessionConnected(session);
        } else if (state === 'failed') {
          session.status = 'failed';
          console.log(`[IceRacing] ${raceId} ICE failed`);
        }
      };

      // Handle incoming tracks (video and audio)
      pc.ontrack = (event) => {

        // Initialize remoteStream if needed
        if (!session.remoteStream) {
          session.remoteStream = event.streams[0] || new MediaStream();
        }

        // Add track to remoteStream if not already present
        const existingTrack = session.remoteStream.getTracks().find(t => t.id === event.track.id);
        if (!existingTrack) {
          session.remoteStream.addTrack(event.track);
        }
      };

      // Handle incoming data channels from Host
      // 关键时序：必须等 data 和 quality-test 都就绪后才发 quality_ready
      // 否则 Host 在 quality-test 的 onmessage 设好之前就开始发包，数据全部丢失
      let dataChannelReady = false;
      let qualityTestReady = false;
      const trySendQualityReady = () => {
        if (dataChannelReady && qualityTestReady && session.dataChannel) {
          try {
            session.dataChannel.send(JSON.stringify({ type: 'quality_ready', raceId }));
          } catch (e) {
            console.warn(`[IceRacing] ${raceId} failed to send ready signal:`, e);
          }
        }
      };

      pc.ondatachannel = (event) => {
        const channel = event.channel;

        // Host creates 'data' channel (reliable)
        if (channel.label === 'data') {
          session.dataChannel = channel;
          // 早期消息缓冲：从 ondatachannel 触发到 WebRTCService.setupDataChannelEvents()
          // 真正挂上业务 onmessage 之间的窗口内，host 可能已经在 1Hz 推 server_stats。
          // Chrome 对 RTCDataChannel.onmessage 属性赋值前到达的 MessageEvent 不会缓冲补发，
          // 这里先挂一个临时 onmessage 把消息存起来，winner 接管后由 setupDataChannelEvents 回放。
          const earlyBuf: MessageEvent[] = [];
          channel.onmessage = (ev) => {
            earlyBuf.push(ev);
          };
          // 把 buffer 挂到 channel 对象上，让 WebRTCService 接管时取到
          (channel as RTCDataChannel & { __earlyBuffer?: MessageEvent[] }).__earlyBuffer = earlyBuf;
          channel.onopen = () => {
            dataChannelReady = true;
            trySendQualityReady();
          };
          // 可能 ondatachannel 触发时已经是 open 状态
          if (channel.readyState === 'open') {
            dataChannelReady = true;
            trySendQualityReady();
          }
        }

        // Host creates 'mouse' channel (unreliable)
        if (channel.label === 'mouse') {
          session.mouseChannel = channel;
        }

        // Host creates 'quality-test' channel (unreliable)
        if (channel.label === 'quality-test') {
          channel.onmessage = (ev) => {
            try {
              let data: string;
              if (typeof ev.data === 'string') {
                data = ev.data;
              } else if (ev.data instanceof ArrayBuffer) {
                const bytes = new Uint8Array(ev.data);
                let endPos = bytes.length;
                for (let i = 0; i < bytes.length; i++) {
                  if (bytes[i] === 0) {
                    endPos = i;
                    break;
                  }
                }
                data = new TextDecoder().decode(bytes.subarray(0, endPos));
              } else {
                return;
              }

              const msg = JSON.parse(data);
              if (msg.type === 'quality_test') {
                this.handleQualityPacket(session, msg);
              }
            } catch (e) {
              // Not JSON or not a quality packet, ignore
            }
          };
          qualityTestReady = true;
          trySendQualityReady();
        }

        // racing 阶段只处理 data/mouse/quality-test；其它通道（如 file-ctrl/file-data）
        // 由 winner 交接后的 WebRTCService.ondatachannel 处理，这里仅记录不拦截
        if (channel.label !== 'data' && channel.label !== 'mouse' && channel.label !== 'quality-test') {
          console.warn(`[IceRacing] ${raceId} racing 阶段收到非 racing 通道 label="${channel.label}"，将由 winner 交接后处理（若开在 winner 候选上需关注是否丢失）`);
        }
      };

      // NOTE: Host creates 'data', 'mouse', 'quality-test' channels
      // Client creates fs-rpc before createAnswer (included in SDP, avoids in-band creation issues)
      // file-ctrl/file-data are lazy-created on demand


      // Set remote description
      await pc.setRemoteDescription(offer);


      // Apply codec preferences based on URL parameter
      this.applyCodecPreferences(pc);

      // Flush any pending candidates that arrived before PC was ready
      await this.flushPendingCandidates(session);

      // 在 createAnswer 前创建 fs-rpc，确保包含在 answer SDP 中
      session.fsRpcChannel = pc.createDataChannel('fs-rpc', {
        ordered: true,
        priority: 'medium',
      });

      // Create and send answer
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);


      session.status = 'racing';

      // Send answer to Host
      signalingService.send({
        type: 'webrtcviewer',
        kind: 'answer',
        raceId,
        message: answer,
      });

      console.log(`[IceRacing] Sent answer for ${raceId}`);

    } catch (e) {
      console.error(`[IceRacing] Failed to handle offer for ${raceId}:`, e);
      session.status = 'failed';
    }
  }

  /**
   * Handle ICE candidate from Host for a specific raceId
   */
  async handleCandidate(raceId: string, candidate: RTCIceCandidateInit): Promise<void> {
    const session = this.sessions.get(raceId);
    if (!session) {
      this._warn('Session not found for candidate:', raceId);
      return;
    }

    // 局域网专用模式：丢弃 Host 发来的 srflx/relay 远端候选，只保留 host，
    // 否则 policy='all' 会跟 Host 的 relay 候选配对而绕外网。空 candidate（哨兵）放行。
    if (this.lanOnly) {
      const typ = this.parseCandidateType(candidate);
      if (typ !== null && typ !== 'host') {
        console.log(`[IceRacing] LAN-Only: drop remote ${typ} candidate for ${raceId}`);
        return;
      }
    }

    // Count remote candidates
    session.remoteCandidateCount++;

    if (!session.pc) {
      // Queue candidate for later - PC not ready yet
      session.pendingCandidates.push(candidate);
      console.log(`[IceRacing] Queued candidate for ${raceId} (PC not ready), count: ${session.remoteCandidateCount}`);
      return;
    }

    try {
      await session.pc.addIceCandidate(new RTCIceCandidate(candidate));
      console.log(`[IceRacing] Added candidate for ${raceId}, count: ${session.remoteCandidateCount}`);
    } catch (e) {
      console.warn(`[IceRacing] Failed to add candidate for ${raceId}:`, e);
    }
  }

  /**
   * Flush pending candidates for a session
   */
  private async flushPendingCandidates(session: RacingSession): Promise<void> {
    if (!session.pc || session.pendingCandidates.length === 0) return;

    console.log(`[IceRacing] Flushing ${session.pendingCandidates.length} pending candidates for ${session.raceId}`);

    for (const candidate of session.pendingCandidates) {
      try {
        await session.pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (e) {
        console.warn(`[IceRacing] Failed to add queued candidate for ${session.raceId}:`, e);
      }
    }
    session.pendingCandidates = [];
  }

  /**
   * Reset racing state
   */
  private reset(): void {
    // Clear timers
    if (this.finalTimer) {
      clearTimeout(this.finalTimer);
      this.finalTimer = null;
    }
    if (this.connectTimer) {
      clearTimeout(this.connectTimer);
      this.connectTimer = null;
    }
    if (this.statusTimer) {
      clearInterval(this.statusTimer);
      this.statusTimer = null;
    }
    if (this.qualityReportTimer) {
      clearInterval(this.qualityReportTimer);
      this.qualityReportTimer = null;
    }

    // Close all existing sessions
    this.sessions.forEach(sess => {
      if (sess.pc) {
        sess.pc.close();
      }
    });

    this.sessions.clear();
    this.winner = null;
    this.winReason = null;
    this.isRacing = false;
    this.qualityTestStarted = false;
    this.qualityTestExtended = false;
    this.relayFirstMode = false;
  }

  /**
   * Cancel racing
   */
  cancel(): void {
    if (!this.isRacing) return;

    this._log('Racing cancelled');

    // Notify Host
    signalingService.send({
      type: 'race_cancel',
    });

    this.reset();
    eventBus.emit('race:cancelled', undefined);
  }

  /**
   * Get current racing sessions for UI display
   */
  getSessions(): RacingSession[] {
    return Array.from(this.sessions.values());
  }

  /**
   * 构造 race:status 的 payload（broadcastStatus 与 lastRaceSnapshot 共用）
   */
  private buildStatusPayload(): import('./EventBus').EventMap['race:status'] {
    const allSessions = Array.from(this.sessions.values());
    const sessions = allSessions.map(s => ({
      raceId: s.raceId,
      label: s.label,
      status: s.status,
      iceState: s.iceState,
      connectionType: s.connectionType,
      connectTime: s.connectTime,
      isWinner: this.winner?.raceId === s.raceId,
      remoteCandidateCount: s.remoteCandidateCount,
      turnServer: s.turnServer,
      quality: s.qualityStats ? {
        packetsReceived: s.qualityStats.packetsReceived,
        packetsLost: s.qualityStats.packetsLost,
        throughputKbps: s.qualityStats.throughputKbps,
        score: s.qualityStats.score,
        rtt: s.qualityStats.rtt,
        rttJitterMs: s.qualityStats.rttJitterMs,
        stableThroughputKbps: s.qualityStats.stableThroughputKbps,
      } : undefined,
    }));

    const hasTestingSessions = allSessions.some(s => s.status === 'testing');
    const phase = this.winner ? 'completed' : (hasTestingSessions ? 'testing' : 'connecting');

    return {
      elapsedMs: Date.now() - this.raceStartTime,
      phase,
      sessions,
      winner: this.winner?.raceId,
      winReason: this.winReason ?? undefined,
      thresholds: {
        minThroughputKbps: this.QUALIFY_MIN_THROUGHPUT_KBPS,
        maxLossPct: this.QUALIFY_MAX_LOSS_PCT,
        maxJitterMs: this.QUALIFY_MAX_JITTER_MS,
      },
    };
  }

  /**
   * Broadcast current racing status for UI
   */
  private broadcastStatus(): void {
    eventBus.emit('race:status', this.buildStatusPayload());
  }

  /**
   * Send final status update after winner declared
   */
  private broadcastFinalStatus(): void {
    this.broadcastStatus();
    // Stop status updates after final broadcast
    if (this.statusTimer) {
      clearInterval(this.statusTimer);
      this.statusTimer = null;
    }
  }
}

// Singleton instance
export const iceRacingService = new IceRacingService();
export default iceRacingService;
